/**
 * Client API V2 — fetch wrapper avec credentials inclus.
 *
 * Cookies httpOnly JWT envoyés automatiquement (credentials: 'include').
 * Le cookie est défini avec domain=.andalous-elearning.com côté API,
 * donc partagé entre cockpit.* (V1) et cockpit-v2.* (V2) — single login.
 *
 * Réponse standardisée : { data: T | null, error: { code, message } | null }
 */

export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiClientError extends Error {
  readonly status: number;
  readonly error: ApiError;

  constructor(status: number, error: ApiError) {
    super(`[API ${status}] ${error.code}: ${error.message}`);
    this.status = status;
    this.error = error;
  }
}

interface ApiEnvelope<T> {
  data: T | null;
  error: ApiError | null;
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  let body: ApiEnvelope<T>;
  try {
    body = (await res.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiClientError(res.status, {
      code: 'invalid_response',
      message: `Réponse non-JSON (HTTP ${res.status})`,
    });
  }

  if (!res.ok || body.error) {
    throw new ApiClientError(
      res.status,
      body.error ?? { code: 'unknown_error', message: 'Erreur inconnue' },
    );
  }
  if (body.data === null) {
    throw new ApiClientError(res.status, {
      code: 'empty_response',
      message: 'Réponse vide',
    });
  }
  return body.data;
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  patch: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, {
      method: 'PATCH',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};
